/* global OT */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import Footer from './Footer';
import InstagramCarousel from './InstagramCarousel';
import { BRAND, CONTACT, whatsappLink } from '../brand';
import {
    Typography, Box, CircularProgress, Button,
    IconButton, Chip, Tooltip, Avatar,
} from '@mui/material';
import {
    VideoCall as VideoCallIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    EventAvailable as EventIcon,
    WhatsApp as WhatsAppIcon,
    Store as StoreIcon,
    PlayArrow as PlayArrowIcon,
    FiberManualRecord as LiveDotIcon,
    Info as InfoIcon,
    Search as SearchIcon,
    Close as CloseIcon,
    AccountCircle as AccountCircleIcon,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import videocallshopApi from '../apis/videocallshop';
import { fetchStores } from '../actions/storeActions';
import { isStoreOpen, useStoreHours } from '../hooks/useStoreHours';
import {
    byStatus,
    CategoryBlock,
    CategoryChips,
    Chip as FilterChip,
    OpenStoresRail,
    SectionHeader,
    useCategoryBlocks,
} from './home/homeSections';
import ProductCard from './storefront/ProductCard';
import CartButton from './cart/CartButton';

const LIVE_CALLS_POLL_INTERVAL_MS = 30_000;

const HERO_SLIDES = [
    {
        image: '/slide1.jpg',
   tag: 'Nueva función',
        title: 'Conectate a\nnuestros live shopping',
        subtitle: 'Mirá los nuevos productos y novedades de cada local.',
        accentColor: '#29D2AB',
        hideText: false,
        overlayLeft: 0.88,
        overlayBottom: 1,
        backgroundPosition: 'center top',
        backgroundSize: 'cover',
    },
    {
        image: '/hero-background.jpg',
        tag: 'Asesoría Personalizada',
        title: 'Conectá con\nExpertos en Vivo',
        subtitle: 'Construcción, Energías Renovables, Inversiones, Ropa y muchos rubros más. Comprá con guía experta sin moverte de tu casa.',
        accentColor: '#29D2AB',
        hideText: false,
        overlayLeft: 0.88,
        overlayBottom: 1,
        backgroundPosition: 'center top',
        backgroundSize: 'cover',
    },
];

const CATEGORIES = [
    { key: 'Tiendas', label: 'Tiendas', icon: <StoreIcon sx={{ fontSize: 20 }} />, accent: '#29D2AB' },
];


// ── Live Card ────────────────────────────────────────────────────────────────
const LiveCard = ({ liveCall, store, onCallClick }) => {
    const cardRef = useRef(null);
    const thumbRef = useRef(null);
    const sessionRef = useRef(null);
    const [streamReady, setStreamReady] = useState(false);
    const [imgError, setImgError] = useState(false);
    // Sólo mira el vivo mientras la card está a la vista: cada preview es un
    // subscriber de OpenTok, que se cobra por minuto.
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const el = cardRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') {
            setInView(true);
            return undefined;
        }
        const observer = new IntersectionObserver(
            (entries) => setInView(entries.some((entry) => entry.isIntersecting)),
            { threshold: 0.4 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const handler = (e) => {
            if (
                e?.message?.includes('play method is not allowed') ||
                e?.message?.includes('play() request was interrupted') ||
                e?.reason?.message?.includes('play method is not allowed') ||
                e?.reason?.message?.includes('play() request was interrupted')
            ) {
                e.preventDefault();
                e.stopImmediatePropagation();
                return false;
            }
        };
        window.addEventListener('error', handler, true);
        window.addEventListener('unhandledrejection', handler, true);
        return () => {
            window.removeEventListener('error', handler, true);
            window.removeEventListener('unhandledrejection', handler, true);
        };
    }, []);

    useEffect(() => {
        if (!store?.storeId || !inView) return;
        let cancelled = false;

        // Vista previa pública y muda (sin login); entrar al vivo sí lo pide.
        videocallshopApi.get(`/stores/${store.storeId}/live-calls/preview-session`)
            .then(({ data }) => {
                if (cancelled) return;
                const sd = data?.data;
                if (!sd?.active || !sd?.tokboxApiKey || !sd?.tokboxSessionId || !sd?.tokboxTokenViewer) return;
                if (typeof OT === 'undefined') return;

                const session = OT.initSession(sd.tokboxApiKey, sd.tokboxSessionId);
                sessionRef.current = session;

                session.on('streamCreated', (event) => {
                    if (!thumbRef.current || cancelled) return;
                    const subscriber = session.subscribe(event.stream, thumbRef.current, {
                        insertMode: 'replace',
                        width: '100%',
                        height: '100%',
                        showControls: false,
                        fitMode: 'cover',
                        subscribeToAudio: false,
                    });

                    subscriber.on('videoElementCreated', (e) => {
                        const video = e.element;
                        video.muted = true;
                        video.setAttribute('muted', '');
                        video.autoplay = true;
                        video.setAttribute('autoplay', '');
                        video.setAttribute('playsinline', '');
                        setTimeout(() => {
                            video.play().catch(() => {});
                        }, 150);
                    });

                    setStreamReady(true);
                });

                session.connect(sd.tokboxTokenViewer, (err) => {
                    if (err) console.warn('LiveCard connect error:', err.message);
                });
            })
            .catch(() => {});

        return () => {
            cancelled = true;
            setStreamReady(false);
            if (sessionRef.current) {
                sessionRef.current.disconnect();
                sessionRef.current = null;
            }
        };
    }, [store?.storeId, inView]);

    if (!store) return null;

    return (
        <Box ref={cardRef} onClick={() => onCallClick(store.storeId)} sx={{
            flexShrink: 0,
            width: { xs: 200, sm: 260 },
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#13233A',
            border: '1px solid rgba(255,68,68,0.3)',
            cursor: 'pointer',
            transition: 'border-color 0.2s, transform 0.2s',
            '&:hover': { borderColor: 'rgba(255,68,68,0.7)', transform: 'translateY(-3px)' },
        }}>
            <Box sx={{
                position: 'relative',
                width: '100%',
                paddingTop: '177%',
                backgroundColor: '#13233A',
                overflow: 'hidden',
            }}>
                {/* Fallback imagen mientras carga el stream */}
                {!streamReady && store.profileImageUrl && !imgError && (
                    <Box component="img" src={store.profileImageUrl} alt={store.name}
                        onError={() => setImgError(true)}
                        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.7 }} />
                )}

                {/* Contenedor donde OpenTok inyecta el video */}
                <Box ref={thumbRef} sx={{
                    position: 'absolute', inset: 0,
                    opacity: streamReady ? 1 : 0,
                    transition: 'opacity 0.5s ease',
                    pointerEvents: 'none',
                    '& .OT_subscriber': { width: '100% !important', height: '100% !important' },
                    '& video': { objectFit: 'cover !important' },
                }} />

                {/* Overlay clickeable encima del video */}
                <Box sx={{ position: 'absolute', inset: 0, zIndex: 10 }} />

                {/* Badge EN VIVO */}
                <Box sx={{
                    position: 'absolute', top: 7, left: 7, zIndex: 20,
                    backgroundColor: '#ff4444', display: 'flex', alignItems: 'center', gap: '4px',
                    px: '7px', py: '2px', borderRadius: '3px',
                }}>
                    <Box sx={{
                        width: 5, height: 5, borderRadius: '50%', backgroundColor: 'white',
                        animation: 'liveblink 1.2s infinite',
                        '@keyframes liveblink': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.15 } },
                    }} />
                    <Typography sx={{ color: 'white', fontSize: 9, fontWeight: 700, letterSpacing: 1 }}>EN VIVO</Typography>
                </Box>

                {/* Nombre de la tienda sobre el video abajo */}
                <Box sx={{
                    position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 20,
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                    px: 1.5, pt: 3, pb: 1,
                }}>
                    <Typography sx={{ color: 'white', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {store.name}
                    </Typography>
                    {store.categoryName && (
                        <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 10 }}>
                            {store.categoryName}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Botón */}
            <Box component="button" sx={{
                width: '100%', backgroundColor: '#ff4444', color: 'white',
                border: 'none', borderRadius: '0 0 8px 8px', padding: '8px 0',
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                '&:hover': { backgroundColor: '#cc0000' }, transition: 'background 0.2s',
            }}>
                <VideoCallIcon sx={{ fontSize: 14 }} />
                Ver en vivo
            </Box>
        </Box>
    );
};

// ── Live Section ─────────────────────────────────────────────────────────────
const LiveSection = ({ liveCalls, stores, onCallClick }) => {
    const scrollRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const liveStores = useMemo(() =>
        liveCalls
            .map(lc => ({ liveCall: lc, store: Object.values(stores).find(s => s.storeId === lc.storeId) }))
            .filter(({ store }) => !!store),
        [liveCalls, stores]
    );

    const checkScroll = () => {
        if (!scrollRef.current) return;
        setCanScrollLeft(scrollRef.current.scrollLeft > 0);
        setCanScrollRight(scrollRef.current.scrollLeft + scrollRef.current.clientWidth < scrollRef.current.scrollWidth - 4);
    };

    useEffect(() => {
        checkScroll();
    }, [liveStores]);

    const scroll = (dir) => {
        if (!scrollRef.current) return;
        scrollRef.current.scrollBy({ left: dir === 'left' ? -340 : 340, behavior: 'smooth' });
        setTimeout(checkScroll, 400);
    };

    if (liveStores.length === 0) return null;

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, pt: 4, pb: 2 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <LiveDotIcon sx={{ fontSize: 10, color: '#ff4444', animation: 'liveblink2 1.2s infinite', '@keyframes liveblink2': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.2 } } }} />
                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '1rem', md: '1.15rem' }, letterSpacing: 0.5, fontFamily: '"Poppins", sans-serif', textTransform: 'uppercase' }}>
                    En vivo ahora
                </Typography>
                <Typography variant="caption" sx={{ color: '#ff4444', fontWeight: 600, fontSize: '0.7rem', border: '1px solid rgba(255,68,68,0.4)', borderRadius: '4px', px: 0.8, py: 0.2 }}>
                    {liveStores.length} tienda{liveStores.length > 1 ? 's' : ''}
                </Typography>
                <Box sx={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, rgba(255,68,68,0.4) 0%, transparent 100%)' }} />
            </Box>

            {/* Cards con flechas */}
            <Box sx={{ position: 'relative' }}>
                {canScrollLeft && (
                    <IconButton onClick={() => scroll('left')} sx={{
                        position: 'absolute', left: -16, top: '40%', transform: 'translateY(-50%)', zIndex: 30,
                        backgroundColor: 'rgba(0,0,0,0.8)', border: '1px solid rgba(255,68,68,0.4)',
                        color: 'white', '&:hover': { backgroundColor: '#ff4444' },
                    }}>
                        <ChevronLeftIcon />
                    </IconButton>
                )}

                <Box ref={scrollRef} onScroll={checkScroll} sx={{
                    display: 'flex', gap: 2, overflowX: 'auto', pb: 2,
                    justifyContent: liveStores.length === 1 ? 'center' : 'flex-start',
                    scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
                    scrollSnapType: 'x mandatory',
                    '& > *': { scrollSnapAlign: 'center' },
                }}>
                    {liveStores.map(({ liveCall, store }) => (
                        <LiveCard key={liveCall.liveCallId} liveCall={liveCall} store={store} onCallClick={onCallClick} />
                    ))}
                </Box>

                {canScrollRight && (
                    <IconButton onClick={() => scroll('right')} sx={{
                        position: 'absolute', right: -16, top: '40%', transform: 'translateY(-50%)', zIndex: 30,
                        backgroundColor: 'rgba(0,0,0,0.8)', border: '1px solid rgba(255,68,68,0.4)',
                        color: 'white', '&:hover': { backgroundColor: '#ff4444' },
                    }}>
                        <ChevronRightIcon />
                    </IconButton>
                )}
            </Box>
        </Box>
    );
};

// ── Search Bar ───────────────────────────────────────────────────────────────
const SearchBar = ({ value, onChange }) => {
    const inputRef = useRef(null);
    const [focused, setFocused] = useState(false);

    return (
        <Box sx={{
            px: { xs: 2, md: 6 }, py: { xs: 6, md: 10 },
            backgroundColor: '#0F1B2D',
            backgroundImage: 'radial-gradient(ellipse at center, rgba(29,152,231,0.08) 0%, rgba(15,27,45,0) 70%)',
        }}>
            <Box sx={{ maxWidth: 850, mx: 'auto', position: 'relative' }}>
                <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, fontSize: { xs: '1.6rem', md: '2.2rem' }, letterSpacing: 1.5, color: 'white', mb: 3, textAlign: 'center', textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>
                    ¿Qué estás buscando?
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: focused ? '#1A2E4A' : '#13233A', borderRadius: '50px', border: focused ? '2px solid #1D98E7' : '2px solid rgba(255,255,255,0.15)', boxShadow: focused ? '0 0 0 4px rgba(29,152,231,0.15), 0 12px 40px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.4)', transition: 'all 0.3s ease', px: { xs: 3, md: 4 }, py: { xs: 1, md: 1.5 }, gap: 2 }}>
                    <SearchIcon sx={{ color: focused ? '#1D98E7' : '#888', fontSize: { xs: 28, md: 34 }, flexShrink: 0, transition: 'color 0.2s' }} />
                    <input ref={inputRef} value={value} onChange={e => onChange(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
                        placeholder="Buscar tiendas, productos, rubros..."
                        style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'white', fontSize: '1.2rem', fontFamily: '"Poppins", sans-serif', fontWeight: 400, padding: '12px 4px', caretColor: '#1D98E7', width: '100%' }}
                    />
                    {value && (
                        <IconButton size="medium" onClick={() => { onChange(''); inputRef.current?.focus(); }} sx={{ color: '#888', '&:hover': { color: 'white' }, flexShrink: 0 }}>
                            <CloseIcon sx={{ fontSize: 24 }} />
                        </IconButton>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center', mt: 3 }}>
                    {CATEGORIES.map(cat => (
                        <Chip key={cat.key} label={cat.label}
                            icon={<Box sx={{ color: `${cat.accent} !important`, display: 'flex', '& svg': { fontSize: '16px !important' } }}>{cat.icon}</Box>}
                            onClick={() => onChange(cat.key)}
                            sx={{ backgroundColor: value === cat.key ? `${cat.accent}22` : 'rgba(255,255,255,0.05)', color: value === cat.key ? cat.accent : '#aaa', border: `1px solid ${value === cat.key ? cat.accent : 'rgba(255,255,255,0.1)'}`, fontFamily: '"Poppins", sans-serif', fontSize: '0.85rem', padding: '18px 4px', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { backgroundColor: `${cat.accent}22`, color: cat.accent, borderColor: cat.accent, transform: 'translateY(-2px)' }, '& .MuiChip-icon': { ml: '6px' } }}
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

// ── Netflix Store Card (lista compacta) ──────────────────────────────────────
const NetflixStoreCard = ({ store, onCallClick, onStoreClick, accent = '#1D98E7', sx }) => {
    const [imgError, setImgError] = useState(false);
    const hasImage = store.profileImageUrl && !imgError;
    const isOpen = useStoreHours(store);
    // con catálogo, tocar la card abre la vidriera; sin catálogo la card sigue sin acción propia (se llama con el botón)
    const hasCatalog = store.productCount > 0 && !!onStoreClick;

    return (
        <Box onClick={hasCatalog ? () => onStoreClick(store) : undefined} sx={{ display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: '#1A2E4A', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '8px 12px 8px 8px', width: '100%', maxWidth: 420, boxSizing: 'border-box', transition: 'border-color 0.2s', cursor: hasCatalog ? 'pointer' : 'default', '&:hover': { borderColor: 'rgba(255,255,255,0.25)' }, ...sx }}>
            <Box sx={{ width: 44, height: 44, borderRadius: '6px', overflow: 'hidden', flexShrink: 0, backgroundColor: '#13233A' }}>
                {hasImage ? (
                    <Box component="img" src={store.profileImageUrl} alt={store.name} onError={() => setImgError(true)} sx={{ width: 44, height: 44, objectFit: 'cover', display: 'block' }} />
                ) : (
                    <Box sx={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <StoreIcon sx={{ fontSize: 22, color: '#555' }} />
                    </Box>
                )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'white', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {store.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center', mt: '2px', flexWrap: 'wrap' }}>
                    {store.categoryName && <Typography sx={{ fontSize: 10, color: '#aaa' }}>{store.categoryName}</Typography>}
                    <Box sx={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: '#555', flexShrink: 0 }} />
                    {store.isLive && (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                <LiveDotIcon sx={{ fontSize: 8, color: '#ff4444' }} />
                                <Typography sx={{ fontSize: 9, color: '#ff4444', fontWeight: 700, letterSpacing: 0.5 }}>EN VIVO</Typography>
                            </Box>
                            <Box sx={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: '#555', flexShrink: 0 }} />
                        </>
                    )}
                    <Typography sx={{ fontSize: 9, fontWeight: 500, borderRadius: '3px', padding: '1px 5px', backgroundColor: isOpen ? 'rgba(41,210,171,0.15)' : 'rgba(226,75,74,0.15)', color: isOpen ? '#29D2AB' : '#e24b4a' }}>
                        {isOpen ? 'Abierto' : 'Cerrado'}
                    </Typography>
                    {hasCatalog && (
                        <Typography sx={{ fontSize: 9, color: '#5BB6EE', fontWeight: 500 }}>
                            {store.productCount} producto{store.productCount !== 1 ? 's' : ''}
                        </Typography>
                    )}
                </Box>
            </Box>
            <Box component="button" onClick={e => { e.stopPropagation(); if (isOpen) onCallClick(store.storeId); }} disabled={!isOpen}
                sx={{ background: isOpen ? '#29D2AB' : '#333', color: isOpen ? '#0F1B2D' : '#666', border: 'none', borderRadius: '6px', padding: '6px 10px', fontSize: 11, fontWeight: 600, cursor: isOpen ? 'pointer' : 'not-allowed', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '4px', transition: 'background 0.2s', '&:hover': isOpen ? { background: '#1FB592' } : {} }}>
                <VideoCallIcon sx={{ fontSize: 14 }} />
                Llamar
            </Box>
        </Box>
    );
};

// ── Search Results ───────────────────────────────────────────────────────────
// Tiendas: filtro local. Productos: GET /products/search (la API ignora menos
// de 2 letras), con 300 ms de espera desde la última tecla.
const MIN_PRODUCT_QUERY = 2;
const SEARCH_DEBOUNCE_MS = 300;

const useProductSearch = (searchTerm) => {
    const [products, setProducts] = useState([]);
    const [searching, setSearching] = useState(false);
    const q = searchTerm.trim();

    useEffect(() => {
        if (q.length < MIN_PRODUCT_QUERY) {
            setProducts([]);
            setSearching(false);
            return;
        }
        let alive = true;
        setSearching(true);
        const t = setTimeout(() => {
            videocallshopApi.get('/products/search', { params: { q, limit: 24 } })
                .then(({ data }) => alive && setProducts(data.data.items))
                .catch(() => alive && setProducts([]))
                .finally(() => alive && setSearching(false));
        }, SEARCH_DEBOUNCE_MS);
        return () => {
            alive = false;
            clearTimeout(t);
        };
    }, [q]);

    return { products, searching };
};

const SearchResults = ({ searchTerm, stores, liveCalls, onCallClick, onStoreClick }) => {
    const { products, searching } = useProductSearch(searchTerm);
    const liveStoreIds = useMemo(() => new Set(liveCalls.map(lc => lc.storeId)), [liveCalls]);
    const results = useMemo(() => {
        if (!searchTerm.trim()) return [];
        const q = searchTerm.toLowerCase();
        return Object.values(stores).filter(s =>
            (s.name && s.name.toLowerCase().includes(q)) ||
            (s.description && s.description.toLowerCase().includes(q)) ||
            (s.categoryName && s.categoryName.toLowerCase().includes(q)) ||
            (s.tags && s.tags.some(t => t.toLowerCase().includes(q)))
        ).map(s => ({ ...s, isLive: liveStoreIds.has(s.storeId) }));
    }, [searchTerm, stores, liveStoreIds]);

    if (!searchTerm.trim()) return null;

    const plural = (n, word) => `${n} ${word}${n !== 1 ? 's' : ''}`;
    const total = results.length + products.length;
    const sectionSx = { color: 'white', fontWeight: 700, fontSize: '1.1rem', mb: 1.5, fontFamily: '"Poppins", sans-serif' };

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
            <Typography sx={{ color: '#888', fontSize: '0.85rem', mb: 3, fontFamily: '"Poppins", sans-serif' }}>
                {total === 0
                    ? searching ? `Buscando "${searchTerm}"…` : `Sin resultados para "${searchTerm}"`
                    : `${plural(products.length, 'producto')} y ${plural(results.length, 'tienda')} para "${searchTerm}"`}
            </Typography>
            {products.length > 0 && (
                <Box sx={{ mb: 4 }}>
                    <Typography sx={sectionSx}>Productos</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5 }}>
                        {products.map(product => <ProductCard key={product.productId} product={product} store={product.store} />)}
                    </Box>
                </Box>
            )}
            {products.length > 0 && results.length > 0 && <Typography sx={sectionSx}>Tiendas</Typography>}
            {total === 0 && searching ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={28} sx={{ color: '#1D98E7' }} />
                </Box>
            ) : total === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <SearchIcon sx={{ fontSize: 56, color: '#333', mb: 2 }} />
                    <Typography sx={{ color: '#555', fontFamily: '"Poppins", sans-serif' }}>Probá con otro término o explorá los rubros de abajo</Typography>
                </Box>
            ) : results.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {results.map(store => <NetflixStoreCard key={store.storeId} store={store} onCallClick={onCallClick} onStoreClick={onStoreClick} accent="#1D98E7" />)}
                </Box>
            )}
        </Box>
    );
};

// ── Hero Slideshow ───────────────────────────────────────────────────────────
const HeroSlideshow = ({ onScrollToStores, onScrollToHowItWorks, liveCalls }) => {
    const [current, setCurrent] = useState(0);
    const [visible, setVisible] = useState(true);
    const timerRef = useRef(null);

    const goTo = (idx) => { setVisible(false); setTimeout(() => { setCurrent(idx); setVisible(true); }, 450); };
    const startTimer = () => {
        clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setVisible(false);
            setTimeout(() => { setCurrent(prev => (prev + 1) % HERO_SLIDES.length); setVisible(true); }, 450);
        }, 7000);
    };
    useEffect(() => { startTimer(); return () => clearInterval(timerRef.current); }, []);
    const handleNav = (idx) => { goTo(idx); startTimer(); };
    const slide = HERO_SLIDES[current];

    return (
        <Box sx={{ position: 'relative', height: { xs: '75vh', md: '88vh' }, overflow: 'hidden', backgroundColor: '#0F1B2D' }}>
            <Box sx={{ position: 'absolute', inset: 0, backgroundImage: `url(${slide.image})`, backgroundSize: slide.backgroundSize || 'cover', backgroundPosition: slide.backgroundPosition || 'center', backgroundRepeat: 'no-repeat', opacity: visible ? 1 : 0, transition: 'opacity 0.45s ease', zIndex: 0 }} />
            {slide.overlayLeft > 0 && <Box sx={{ position: 'absolute', inset: 0, zIndex: 1, background: `linear-gradient(90deg, rgba(0,0,0,${slide.overlayLeft}) 0%, rgba(0,0,0,${slide.overlayLeft * 0.6}) 45%, rgba(0,0,0,0.05) 100%)` }} />}
            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '35%', zIndex: 1, background: `linear-gradient(transparent, rgba(15,27,45,${slide.overlayBottom}))` }} />

            {!slide.hideText && (
                <Box sx={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', pb: { xs: 8, md: 11 }, px: { xs: 3, sm: 5, md: 9 }, maxWidth: { xs: '100%', md: '54%' }, opacity: visible ? 1 : 0, transition: 'opacity 0.45s ease' }}>
                    <Box component="img" src="/videocallshop-logo.png" alt="VideoCallShop" sx={{ height: { xs: 48, md: 68 }, width: 'auto', mb: 2, alignSelf: 'flex-start', filter: 'drop-shadow(0 2px 10px rgba(0,0,0,0.9))' }} />
                    <Typography sx={{ color: slide.accentColor, fontFamily: '"Poppins", sans-serif', fontWeight: 700, fontSize: { xs: '0.8rem', md: '0.95rem' }, letterSpacing: 3, textTransform: 'uppercase', mb: 1 }}>{slide.tag}</Typography>
                    <Typography component="h1" sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, fontSize: { xs: '2.4rem', sm: '3.2rem', md: '4.2rem' }, lineHeight: 1.05, letterSpacing: '-0.02em', color: 'white', mb: 2.5, whiteSpace: 'pre-line' }}>{slide.title}</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: { xs: '0.9rem', md: '1.05rem' }, fontFamily: '"Poppins", sans-serif', fontWeight: 300, lineHeight: 1.65, mb: 3.5, maxWidth: 460 }}>{slide.subtitle}</Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                        <Button variant="contained" size="large" startIcon={<PlayArrowIcon />} onClick={onScrollToStores} sx={{ backgroundColor: '#29D2AB', color: '#0F1B2D', fontWeight: 700, fontFamily: '"Poppins", sans-serif', px: 4, py: 1.5, fontSize: '1rem', borderRadius: '999px', '&:hover': { backgroundColor: '#1FB592', transform: 'scale(1.03)' }, transition: 'all 0.2s' }}>Ver Tiendas</Button>
                        <Button variant="contained" size="large" startIcon={<InfoIcon />} onClick={onScrollToHowItWorks} sx={{ backgroundColor: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.35)', color: 'white', fontWeight: 700, fontFamily: '"Poppins", sans-serif', px: 4, py: 1.5, fontSize: '1rem', borderRadius: '999px', backdropFilter: 'blur(4px)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.22)', transform: 'scale(1.03)' }, transition: 'all 0.2s' }}>¿Cómo compro?</Button>
                    </Box>
                    {liveCalls.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LiveDotIcon sx={{ fontSize: 12, color: '#ff4444', animation: 'blink 1.5s infinite', '@keyframes blink': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.2 } } }} />
                            <Typography variant="caption" sx={{ color: '#ff4444', fontWeight: 700, letterSpacing: 1.5, fontSize: '0.7rem' }}>{liveCalls.length} TIENDA{liveCalls.length > 1 ? 'S' : ''} EN VIVO AHORA</Typography>
                        </Box>
                    )}
                </Box>
            )}

            {slide.hideText && (
                <Box sx={{ position: 'absolute', bottom: { xs: 28, md: 40 }, left: 0, right: 0, zIndex: 3, display: 'flex', gap: 2, justifyContent: 'center', opacity: visible ? 1 : 0, transition: 'opacity 0.45s ease' }}>
                    <Button variant="contained" size="large" startIcon={<PlayArrowIcon />} onClick={onScrollToStores} sx={{ backgroundColor: '#1D98E7', color: 'white', fontWeight: 700, fontFamily: '"Poppins", sans-serif', px: 4, py: 1.5, fontSize: '1rem', borderRadius: '999px', '&:hover': { backgroundColor: '#1584CC', transform: 'scale(1.03)' }, transition: 'all 0.2s' }}>Ver Tiendas</Button>
                    <Button variant="contained" size="large" startIcon={<InfoIcon />} onClick={onScrollToHowItWorks} sx={{ backgroundColor: 'rgba(15,27,45,0.85)', color: 'white', fontWeight: 700, fontFamily: '"Poppins", sans-serif', px: 4, py: 1.5, fontSize: '1rem', borderRadius: '999px', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.2)', '&:hover': { backgroundColor: 'rgba(19,35,58,0.95)', transform: 'scale(1.03)' }, transition: 'all 0.2s' }}>¿Cómo funciona?</Button>
                </Box>
            )}

            {HERO_SLIDES.length > 1 && (
                <>
                    <IconButton onClick={() => handleNav((current - 1 + HERO_SLIDES.length) % HERO_SLIDES.length)} sx={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', zIndex: 5, backgroundColor: 'rgba(0,0,0,0.5)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.85)' } }}><ChevronLeftIcon /></IconButton>
                    <IconButton onClick={() => handleNav((current + 1) % HERO_SLIDES.length)} sx={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', zIndex: 5, backgroundColor: 'rgba(0,0,0,0.5)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.85)' } }}><ChevronRightIcon /></IconButton>
                </>
            )}
            <Box sx={{ position: 'absolute', bottom: 12, right: 20, zIndex: 5, display: 'flex', gap: 1 }}>
                {HERO_SLIDES.map((_, i) => <Box key={i} onClick={() => handleNav(i)} sx={{ width: i === current ? 24 : 8, height: 4, borderRadius: 2, cursor: 'pointer', backgroundColor: i === current ? 'white' : 'rgba(255,255,255,0.4)', transition: 'all 0.3s ease' }} />)}
            </Box>
        </Box>
    );
};

// ── App Download + Instagram ─────────────────────────────────────────────────
const AppDownloadSection = () => (
    <Box sx={{
        mx: { xs: 2, md: 4 },
        mb: 8,
        borderRadius: '16px',
        p: { xs: 3, md: 6 },
        background: '#13233A',
        border: '1px solid rgba(255,255,255,0.07)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: { xs: 4, md: 6 },
        flexWrap: 'wrap',
    }}>
        {/* Izquierda: título + botones */}
        <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: 320 }}>
            <Typography sx={{
                fontSize: '0.7rem', letterSpacing: 3, textTransform: 'uppercase',
                color: '#29D2AB', fontWeight: 700, mb: 1,
                fontFamily: '"Poppins", sans-serif',
            }}>
                Disponible ahora
            </Typography>
            <Typography sx={{
                fontFamily: '"Poppins", sans-serif', fontWeight: 700,
                fontSize: { xs: '2.4rem', md: '3rem' },
                color: 'white', lineHeight: 0.95, letterSpacing: 1, mb: 1.5,
            }}>
                Bajá la app{' '}
                <Box component="span" sx={{ color: '#29D2AB' }}>gratis</Box>
            </Typography>
            <Typography sx={{
                fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)',
                fontFamily: '"Poppins", sans-serif', fontWeight: 300,
                lineHeight: 1.6, mb: 3,
            }}>
                Conectate con tiendas, mirá lives y comprá desde tu celular en cualquier momento.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Google Play */}
                <Box
                    component="a"
                    href="https://play.google.com/store/apps/details?id=com.anonymous.videocallshopmobile"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        background: '#1A2E4A', border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: '10px', p: '11px 16px', textDecoration: 'none',
                        transition: 'border-color 0.2s, background 0.2s',
                        '&:hover': { borderColor: 'rgba(255,255,255,0.35)', background: '#22385A' },
                    }}
                >
                    {/* Google Play icon SVG inline */}
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                        <path d="M4.5 3.2L15.8 14 4.5 24.8c-.3-.2-.5-.6-.5-1V4.2c0-.4.2-.8.5-1z" fill="#00C853"/>
                        <path d="M19.8 10.2L16.6 14l3.2 3.8 4-2.3c1.1-.6 1.1-2.2 0-2.8l-4-2.5z" fill="#FFCA28"/>
                        <path d="M4.5 3.2l11.3 6.5-3.2 3.8L4.5 3.2z" fill="#F44336"/>
                        <path d="M12.6 14.5l3.2 3.8-11.3 6.5 8.1-10.3z" fill="#4FC3F7"/>
                    </svg>
                    <Box>
                        <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: '"Poppins", sans-serif' }}>Disponible en</Typography>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: 'white', fontFamily: '"Poppins", sans-serif', letterSpacing: 0.5 }}>Google Play</Typography>
                    </Box>
                </Box>

                {/* App Store */}
                <Box
                    component="a"
                    href="https://apps.apple.com/ar/app/videocallshop/id6762129354"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        background: '#1A2E4A', border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: '10px', p: '11px 16px', textDecoration: 'none',
                        transition: 'border-color 0.2s, background 0.2s',
                        '&:hover': { borderColor: 'rgba(255,255,255,0.35)', background: '#22385A' },
                    }}
                >
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                        <path d="M20.3 14.8c0-3 2.5-4.5 2.6-4.6-1.4-2-3.6-2.3-4.4-2.3-1.9-.2-3.6 1.1-4.6 1.1-1 0-2.5-1.1-4.1-1-2.1 0-4 1.2-5.1 3.1-2.2 3.7-.6 9.3 1.6 12.3 1 1.5 2.3 3.2 3.9 3.1 1.6-.1 2.2-1 4.1-1s2.4 1 4.1.9c1.7 0 2.8-1.5 3.9-3 1.2-1.7 1.7-3.4 1.7-3.5-.1 0-3.7-1.4-3.7-5.1z" fill="white"/>
                        <path d="M17.4 5.6c.9-1 1.5-2.5 1.3-3.9-1.3.1-2.8.8-3.7 1.9-.8.9-1.5 2.4-1.3 3.8 1.4.1 2.8-.7 3.7-1.8z" fill="white"/>
                    </svg>
                    <Box>
                        <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: '"Poppins", sans-serif' }}>Descargalo en el</Typography>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: 'white', fontFamily: '"Poppins", sans-serif', letterSpacing: 0.5 }}>App Store</Typography>
                    </Box>
                </Box>
            </Box>
        </Box>

        {/* Divisor vertical */}
        <Box sx={{ width: '1px', height: 180, background: 'rgba(255,255,255,0.08)', flexShrink: 0, display: { xs: 'none', md: 'block' } }} />

        {/* Derecha: QR Instagram */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ background: 'white', borderRadius: '14px', p: '10px', width: 140, height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box component="img" src="/qr.png" alt="QR Instagram VideoCallShop" sx={{ width: 120, height: 120, display: 'block' }} />
            </Box>
            <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center', fontFamily: '"Poppins", sans-serif', letterSpacing: 0.5 }}>
                Escaneá para<br />seguirnos en Instagram
            </Typography>
            <Box component="a" href="https://instagram.com/videocallshop.ok" target="_blank" rel="noopener noreferrer"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '0.85rem', fontWeight: 600, color: '#29D2AB', textDecoration: 'none', fontFamily: '"Poppins", sans-serif', letterSpacing: 0.5, '&:hover': { color: '#5FE0C2' } }}>
                @videocallshop.ok
            </Box>
        </Box>
    </Box>
);




// ── Comercios → landing /negocios ───────────────────────────────
const SELLER_PERKS = [
    'Atención personalizada por videollamada',
    'Cerrá la venta durante la llamada',
    'Envíos integrados y diferentes checkouts',
    'Vendé en vivo como se vende en todo el mundo',
];

const SellerSection = () => (
    <Box sx={{
        mx: { xs: 2, md: 4 }, mb: 8, borderRadius: '20px', p: { xs: 4, md: 6 },
        position: 'relative', overflow: 'hidden',
        background: `linear-gradient(120deg, ${BRAND.night2} 0%, #16365a 100%)`,
        border: '1px solid rgba(41,210,171,0.25)',
    }}>
        <Box sx={{ position: 'absolute', width: 260, height: 260, borderRadius: '50%', background: BRAND.green, right: -110, bottom: -130, opacity: 0.9 }} />
        <Box sx={{ position: 'absolute', width: 200, height: 200, borderRadius: '50%', background: BRAND.blue, right: 60, top: -150, opacity: 0.9 }} />
        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 640 }}>
            <Typography sx={{ color: BRAND.green, fontWeight: 600, fontSize: '0.8rem', letterSpacing: 2, textTransform: 'uppercase', mb: 1 }}>
                Para comercios
            </Typography>
            <Typography component="h2" sx={{ fontWeight: 700, fontSize: { xs: '1.8rem', md: '2.4rem' }, color: 'white', lineHeight: 1.15, mb: 2 }}>
                ¿Tenés un negocio? Vendé en vivo con VideoCallShop
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.78)', fontSize: '1rem', lineHeight: 1.7, mb: 2.5 }}>
                Mostrá tus productos y asesorá a tus clientes por videollamada, desde tu local.
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mb: 3.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                {SELLER_PERKS.map(perk => (
                    <Box component="li" key={perk} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', color: 'rgba(255,255,255,0.9)', fontSize: '0.92rem' }}>
                        <Box component="span" sx={{ color: BRAND.green, fontWeight: 700 }}>✓</Box>{perk}
                    </Box>
                ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Button component={RouterLink} to={`${CONTACT.landingPath}#agenda`}
                    variant="contained" size="large" startIcon={<EventIcon />}
                    sx={{ backgroundColor: BRAND.green, color: BRAND.night, fontWeight: 700, px: 3.5, py: 1.4, borderRadius: '999px', '&:hover': { backgroundColor: BRAND.greenDark } }}>
                    Agendá una reunión
                </Button>
                <Button component={RouterLink} to={`${CONTACT.landingPath}#planes`}
                    variant="outlined" size="large"
                    sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.45)', fontWeight: 600, px: 3, py: 1.4, borderRadius: '999px', '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.06)' } }}>
                    Ver planes
                </Button>
                <Button component="a" href={whatsappLink('¡Hola! Quiero sumar mi comercio a VideoCallShop.')} target="_blank" rel="noopener noreferrer"
                    size="large" startIcon={<WhatsAppIcon />}
                    sx={{ color: 'white', fontWeight: 600, px: 2, py: 1.4, borderRadius: '999px', '& svg': { color: '#25D366' }, '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' } }}>
                    WhatsApp
                </Button>
            </Box>
        </Box>
    </Box>
);

// ── Home ─────────────────────────────────────────────────────────────────────
const Home = ({ callRequest, fetchStores, stores, customer }) => {
    const navigate = useNavigate();
    const [liveCalls, setLiveCalls] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryId, setCategoryId] = useState(null);
    const [onlyOpen, setOnlyOpen] = useState(false);
    const blocks = useCategoryBlocks();

    const scrollToHowItWorks = () => document.getElementById('how-it-works-section')?.scrollIntoView({ behavior: 'smooth' });

    useEffect(() => { if (callRequest.callRequest) navigate('/waiting-room'); }, [callRequest.callRequest, navigate]);
    useEffect(() => { fetchStores(); }, [fetchStores]);

    useEffect(() => {
        const fetchLiveCalls = () => {
            videocallshopApi.get('/live-calls')
                .then(({ data }) => setLiveCalls(data.data || []))
                .catch(() => setLiveCalls([]));
        };
        fetchLiveCalls();
        const id = setInterval(fetchLiveCalls, LIVE_CALLS_POLL_INTERVAL_MS);
        return () => clearInterval(id);
    }, []);

    // las tiendas, con su marca de "en vivo" y de abierta ahora
    const homeStores = useMemo(() => {
        const liveStoreIds = new Set(liveCalls.map(lc => lc.storeId));
        return Object.values(stores.stores).map(s => ({ ...s, isLive: liveStoreIds.has(s.storeId), isOpen: isStoreOpen(s) }));
    }, [stores.stores, liveCalls]);
    const storeById = useMemo(() => new Map(homeStores.map(s => [s.storeId, s])), [homeStores]);

    // chips: cada categoría con al menos una tienda, por nombre
    const categories = useMemo(() => {
        const seen = new Map();
        homeStores.forEach(s => {
            if (s.categoryId != null && s.categoryName && !seen.has(s.categoryId)) seen.set(s.categoryId, { id: s.categoryId, name: s.categoryName });
        });
        return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name, 'es'));
    }, [homeStores]);

    // el chip elegido filtra todas las secciones
    const inCategory = x => categoryId == null || x.categoryId === categoryId;
    const shownLiveCalls = liveCalls.filter(lc => inCategory(storeById.get(lc.storeId) || {}));
    const openStores = homeStores.filter(s => s.isOpen && inCategory(s)).sort(byStatus);
    const shownBlocks = blocks.filter(b => categoryId == null || b.categoryId === categoryId);
    const allStores = homeStores.filter(s => inCategory(s) && (!onlyOpen || s.isOpen)).sort(byStatus);
    const scrollToAllStores = () => document.getElementById('all-stores-section')?.scrollIntoView({ behavior: 'smooth' });

    const goToCreateCallRequest = storeId => navigate(`/${storeId}/create-call-request`);
    // card de tienda: con catálogo va a la vidriera, sin catálogo directo a llamar
    const goToStore = store => (store.productCount > 0 ? navigate(`/tienda/${store.storeId}`) : goToCreateCallRequest(store.storeId));
    const goToLive = storeId => navigate(`/store/${storeId}/live`);


    return (
        <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: '#0F1B2D', color: 'white' }}>

            {/* Mi carrito (sólo con sesión; a la izquierda de Mi cuenta) */}
            <CartButton sx={{ position: 'fixed', top: 16, right: 68, zIndex: 50 }} />

            {/* Mi cuenta */}
            <Tooltip title="Mi cuenta">
                <IconButton
                    onClick={() => navigate('/account')}
                    sx={{
                        position: 'fixed', top: 16, right: 16, zIndex: 50,
                        backgroundColor: 'rgba(0,0,0,0.5)', color: 'white',
                        border: '1px solid rgba(255,255,255,0.2)',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.85)' },
                        p: customer ? 0.5 : 1,
                    }}
                >
                    {customer ? (
                        <Avatar
                            src={customer.profileImageUrl || undefined}
                            imgProps={{ referrerPolicy: 'no-referrer' }}
                            sx={{ width: 32, height: 32, fontSize: 16 }}
                        >
                            {customer.name ? customer.name[0] : '?'}
                        </Avatar>
                    ) : (
                        <AccountCircleIcon />
                    )}
                </IconButton>
            </Tooltip>

            <HeroSlideshow
                onScrollToStores={() => document.getElementById('stores-section')?.scrollIntoView({ behavior: 'smooth' })}
                onScrollToHowItWorks={scrollToHowItWorks}
                liveCalls={liveCalls}
            />
            <AppDownloadSection />

            <Box id="stores-section">
                <SearchBar value={searchTerm} onChange={setSearchTerm} />
            </Box>

            <Box sx={{ pt: 2, pb: 6 }}>
                {stores.isFetching ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                        <CircularProgress sx={{ color: '#1D98E7' }} />
                    </Box>
                ) : searchTerm.trim() ? (
                    <SearchResults searchTerm={searchTerm} stores={stores.stores} liveCalls={liveCalls} onCallClick={goToCreateCallRequest} onStoreClick={goToStore} />
                ) : (
                    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
                        {categories.length > 1 && (
                            <Box sx={{ px: { xs: 2, md: 4 }, pb: 1 }}>
                                <CategoryChips categories={categories} selected={categoryId} onSelect={setCategoryId} />
                            </Box>
                        )}

                        <LiveSection liveCalls={shownLiveCalls} stores={stores.stores} onCallClick={goToLive} />

                        {openStores.length > 0 && (
                            <Box sx={{ px: { xs: 2, md: 4 }, pt: 3, pb: 2 }}>
                                <SectionHeader title="Tiendas abiertas ahora" sub="Entrá y te atienden por videollamada" dot={BRAND.green} action="Ver todas" onAction={scrollToAllStores} />
                                <OpenStoresRail stores={openStores} onCall={goToCreateCallRequest} onOpen={goToStore} />
                            </Box>
                        )}

                        {shownBlocks.length > 0 && (
                            <Box sx={{ px: { xs: 2, md: 4 }, pt: 2 }}>
                                {shownBlocks.map(block => (
                                    <CategoryBlock
                                        key={block.categoryId}
                                        block={block}
                                        stores={block.storeIds.map(id => storeById.get(id)).filter(Boolean)}
                                        onStore={goToStore}
                                    />
                                ))}
                            </Box>
                        )}

                        <Box id="all-stores-section" sx={{ px: { xs: 2, md: 4 }, pt: 3, pb: 2, scrollMarginTop: 16 }}>
                            <SectionHeader
                                title="Todas las tiendas"
                                sub={`${allStores.length} ${allStores.length === 1 ? 'tienda' : 'tiendas'} · ${onlyOpen ? 'sólo abiertas' : 'abiertas primero'}`}
                            />
                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                <FilterChip label="Todas" selected={!onlyOpen} onClick={() => setOnlyOpen(false)} />
                                <FilterChip label="Sólo abiertas" selected={onlyOpen} onClick={() => setOnlyOpen(true)} />
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
                                {allStores.map(store => (
                                    <NetflixStoreCard key={store.storeId} store={store} onCallClick={goToCreateCallRequest} onStoreClick={goToStore} sx={{ maxWidth: 'none' }} />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 8, px: 2 }}>
                <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.8rem' }, mb: 1, color: 'white', letterSpacing: 1 }}>Mirá la experiencia en acción</Typography>
                <Typography sx={{ color: '#888', fontSize: '0.9rem', mb: 3 }}>Cómo comprar en VideoCallShop</Typography>
                <InstagramCarousel />
            </Box>

            <Box id="how-it-works-section" sx={{ px: { xs: 2, md: 4 }, pb: 8 }}>
                <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, fontSize: { xs: '1.8rem', md: '2.4rem' }, letterSpacing: 1, color: 'white', mb: 4 }}>¿Cómo Funciona?</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
                    {[
                        { num: '01', title: 'Elegí tu Tienda', desc: 'Explorá los rubros y encontrá el comercio que te interese.', accent: '#29D2AB' },
                        { num: '02', title: 'Iniciá la Videollamada', desc: 'Conectate en tiempo real con un asesor de ventas.', accent: '#1D98E7' },
                        { num: '03', title: 'Recibí Asesoría Gratis', desc: 'El asesor te guía y responde todas tus preguntas.', accent: '#29D2AB' },
                        { num: '04', title: 'Comprá y Recibí', desc: 'Realizá tu compra y recibí los productos en la puerta de tu casa.', accent: '#5BB6EE' },
                    ].map(step => (
                        <Box key={step.num} sx={{ p: 3, borderRadius: '8px', backgroundColor: '#13233A', border: `1px solid ${step.accent}22`, transition: 'all 0.3s', '&:hover': { borderColor: `${step.accent}66`, transform: 'translateY(-4px)', boxShadow: `0 8px 24px ${step.accent}22` } }}>
                            <Typography sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, fontSize: '2.5rem', color: step.accent, opacity: 0.4, lineHeight: 1, mb: 1 }}>{step.num}</Typography>
                            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem', fontFamily: '"Poppins", sans-serif', letterSpacing: 0.5, mb: 1 }}>{step.title}</Typography>
                            <Typography sx={{ color: '#888', fontSize: '0.82rem', lineHeight: 1.5 }}>{step.desc}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Comercios: lleva a la landing /negocios (agenda, planes, WhatsApp) */}
            <SellerSection />

            <Footer />
        </Box>
    );
};

const mapStateToProps = state => ({ callRequest: state.callRequest, stores: state.stores, customer: state.customer.customer });
export default connect(mapStateToProps, { fetchStores })(Home);